/**
 * PraticheTable — F-005 Phase 4 (IMPL-P4-004).
 *
 * Tabella delle pratiche in corso del cliente. Mirror del layout di
 * `OperatorQueueTable` (HTML `<table>` + Tailwind, no shadcn Table primitives
 * disponibili in questo progetto).
 *
 * Stati:
 *   - `isPending` → 3 righe skeleton (`aria-busy="true"`)
 *   - `items.length === 0` → empty state con messaggio centrato
 *   - else → tabella con header + righe `PraticaListItem`
 *
 * Colonne: Codice, Stato, Aggiornata, Azioni (sr-only per la quarta).
 */
import type { PraticaListItem } from '../lib/cliente-pratiche-client';
import { PraticaListItem as PraticaListItemRow } from './PraticaListItem';

interface PraticheTableProps {
  items: PraticaListItem[];
  isPending: boolean;
}

/**
 * Skeleton riga (no shadcn Skeleton disponibile). Pattern: blocco grigio
 * con classe `animate-pulse` (Tailwind) — equivalente del componente
 * shadcn Skeleton.
 */
function SkeletonRow() {
  return (
    <tr>
      <td colSpan={4} className="px-3 py-2">
        <div
          className="h-8 w-full animate-pulse rounded bg-muted"
          aria-hidden="true"
        />
      </td>
    </tr>
  );
}

export function PraticheTable({ items, isPending }: PraticheTableProps) {
  if (isPending) {
    return (
      <div
        className="overflow-x-auto rounded-md border"
        aria-busy="true"
        aria-label="Caricamento pratiche"
      >
        <table className="w-full text-sm">
          <tbody className="divide-y">
            {[0, 1, 2].map((i) => (
              <SkeletonRow key={i} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        Nessuna pratica in corso.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">
              Codice
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Stato
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Aggiornata
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              <span className="sr-only">Azioni</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item) => (
            <PraticaListItemRow key={item.id} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
